import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getRequest } from "utils/httpClient";
import { RootState } from "../store";
import { getPaynoteFundingSourcesPath } from "utils/backend-path-builders";

import { PaynoteFundingSource } from "interfaces/commerce/paynote/paynote-funding-source.ts";

export interface PaynoteFundingSourcesState {
  fundingSources: PaynoteFundingSource[];
  fetchStatusState: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  isPolling: boolean;
}

const initialState: PaynoteFundingSourcesState = {
  fundingSources: [],
  fetchStatusState: "idle",
  error: null,
  isPolling: false,
};

export const fetchPaynoteFundingSources = createAsyncThunk<
  PaynoteFundingSource[],
  void,
  { rejectValue: string }
>("paynoteFundingSources/fetch", async (_, { rejectWithValue }) => {
  try {
    const response = await getRequest(getPaynoteFundingSourcesPath());
    return response || [];
  } catch (err) {
    return rejectWithValue("Failed to fetch funding sources");
  }
});

const slice = createSlice({
  name: "paynoteFundingSources",
  initialState,
  reducers: {
    clearFundingSourcesState: () => initialState,
    setPolling: (state, action) => {
      state.isPolling = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchPaynoteFundingSources.pending, (state) => {
      state.fetchStatusState = "loading";
      state.error = null;
    });
    builder.addCase(fetchPaynoteFundingSources.fulfilled, (state, action) => {
      state.fetchStatusState = "succeeded";
      state.fundingSources = action.payload;
      state.error = null;
    });
    builder.addCase(fetchPaynoteFundingSources.rejected, (state, action) => {
      state.fetchStatusState = "failed";
      state.error = action.payload ?? "Something went wrong.";
    });
  },
});

export const getPaynoteFundingSourcesState: (state: RootState) => PaynoteFundingSourcesState = (state: RootState) => state.paynoteFundingSources;

export const { clearFundingSourcesState, setPolling } = slice.actions;

export default slice.reducer; 